/**
 * Whether persisted weld-parameter data exists for a floor-sheet weld row.
 * Used by the Parameters button preflight on FloorSheet.
 */
import { passMappingRowsFromKeys, type PassMappingRow } from './weldUpload/passMappingKeys'
export function hasPersistedWeldParams(
  getSection: (key: string) => unknown,
  weldId: string
): boolean {
  const data = getSection(`weldparams.${weldId}`)
  if (!data || typeof data !== 'object') return false

  const record = data as Record<string, unknown>

  const data360 = record.data360
  if (data360 && typeof data360 === 'object') {
    const d360 = data360 as Record<string, unknown>
    if (d360.importedAt) return true
    if (d360.passMappingComplete === false) return true
  }

  if (record.source === 'manual') {
    return Array.isArray(record.rows) && record.rows.length > 0
  }

  if (record.source === 'upload') {
    const aggregate = record.aggregate
    if (aggregate && typeof aggregate === 'object') {
      const rows = (aggregate as Record<string, unknown>).rows
      return Array.isArray(rows) && rows.length > 0
    }
  }

  return false
}

function readData360Meta(
  getSection: (key: string) => unknown,
  weldRowId: string
): Record<string, unknown> | null {
  const data = getSection(`weldparams.${weldRowId}`)
  if (!data || typeof data !== 'object') return null
  const data360 = (data as Record<string, unknown>).data360
  if (!data360 || typeof data360 !== 'object') return null
  return data360 as Record<string, unknown>
}

/** Sorted mapping keys from a Data360 import awaiting user mapping (torch-aware or legacy). */
export function getData360DistinctPassNames(
  getSection: (key: string) => unknown,
  weldRowId: string
): string[] {
  const meta = readData360Meta(getSection, weldRowId)
  if (!meta) return []
  const raw = meta.distinctPassNames
  if (!Array.isArray(raw)) return []
  return [...raw].map((n) => String(n).trim()).filter(Boolean).sort()
}

/** PassMappingPanel rows for Data360 rename — supports legacy plain keys and `Lead:HB` keys. */
export function getData360PassMappingRows(
  getSection: (key: string) => unknown,
  weldRowId: string
): PassMappingRow[] {
  return passMappingRowsFromKeys(getData360DistinctPassNames(getSection, weldRowId))
}

/** True when imported Data360 telemetry still needs Rename Pass on Floor Sheet. */
export function needsData360PassMapping(
  getSection: (key: string) => unknown,
  weldRowId: string
): boolean {
  const meta = readData360Meta(getSection, weldRowId)
  if (!meta || meta.passMappingComplete !== false) return false
  return getData360DistinctPassNames(getSection, weldRowId).length > 0
}
